// export default DairyProductsReport;
import React, { useState } from "react";
import axios from "axios";
import { baseUrl, getDateWithDay, formatDateDMY } from "../../comman/config";
import { Link } from 'react-router-dom';
import { DairyRequirementFilter } from "../../components/Filter/DairyRequirementFilter.js";
import Loader from "../../components/Loader.js";
import { isFilterActive, matchFilterText, patchFilter } from "../../components/Filter/columnFilter";
import ColumnFilterRow from "../../components/Filter/ColumnFilterRow";
import NoRecordsFound from "../../components/NoRecordsFound.js";
import toast, { Toaster } from "react-hot-toast";
import DairyIssueDetailsModal from "../../components/DairyStockModal.js";

function DairyProductsReport() {
    const today = new Date().toISOString().split("T")[0];
    const [reportData, setReportData] = useState([]);
    // Inline per-column filters: { [colKey]: { op, v1, v2 } }. FILTER_COLS lists the 15 body
    // columns in table order and returns each cell's plain text -- the issued and returned cells
    // render links, and a filter has to see the number inside them, not the markup.
    const [filters, setFilters] = useState({});
    const setColFilter = (key, patch) => setFilters((prev) => patchFilter(prev, key, patch));
    const FILTER_COLS = [
        { key: 'date',       text: (r) => formatDateDMY(r.date) },
        { key: 'ingredient', text: (r) => r.ingredient_name },
        { key: 'opening',    text: (r) => r.opening_qty },
        { key: 'order_no',   text: (r) => r.order_no },
        { key: 'order_qty',  text: (r) => r.order_qty },
        { key: 'received',   text: (r) => r.total_received_qty },
        { key: 'pending',    text: (r) => Number(r.pending_qty ?? 0).toFixed(2) },
        { key: 'iss_break',  text: (r) => r.issued_qty?.breakfast },
        { key: 'iss_lunch',  text: (r) => r.issued_qty?.lunch },
        { key: 'iss_noon',   text: (r) => r.issued_qty?.noon },
        { key: 'iss_dinner', text: (r) => r.issued_qty?.dinner },
        { key: 'iss_night',  text: (r) => r.issued_qty?.night },
        { key: 'iss_total',  text: (r) => r.total_issued_qty },
        { key: 'returned',   text: (r) => r.total_returned_qty },
        { key: 'closing',    text: (r) => r.closing_qty },
    ];
    const activeFilterCols = FILTER_COLS.filter((c) => isFilterActive(filters[c.key]));
    const visibleRows = reportData.filter((r) => activeFilterCols.every((c) => matchFilterText(c.text(r), filters[c.key])));
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [ingredient, setIngredient] = useState([]);
    const [orderNo, setOrderNo] = useState({});
    const [session, setSession] = useState({});
    const [loading, setLoading] = useState(false);
    const [noRecordsFound, setNoRecordsFound] = useState(false);

    // State for modal
    const [showIssueModal, setShowIssueModal] = useState(false);
    const [issueDetails, setIssueDetails] = useState([]);
    const [selectedDetail, setSelectedDetail] = useState({
        date: "",
        ingredient_id: null,
        ingredient_name: "",
        session_id: null,
        session_name: ""
    });

    // State for return modal
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnDetails, setReturnDetails] = useState([]);
    const [selectedReturnDetail, setSelectedReturnDetail] = useState({
        date: "",
        ingredient_id: null,
        ingredient_name: "",
        session_id: null,
        session_name: ""
    });

    const handelFilterChange = async (from_date, to_date, order_status, order_id, ingredient_data, receipt_status, session_data) => {
        setFromDate(from_date);
        setToDate(to_date);
        setIngredient(ingredient_data);
        setOrderNo(order_id);
        setSession(session_data); // Changed to session_data for clarity
        // Run the report on the values Submit handed over. Submitting an untouched filter writes
        // the same values back to the state, and a report that only re-ran on a state change would
        // never notice the press.
        if (from_date) {
            getDairyProductsReport({ from_date, to_date, ingredient: ingredient_data, order_no: order_id, session: session_data });
        }
    };

    const getDairyProductsReport = async (filters) => {
        const { from_date, to_date, ingredient: filterIngredient, order_no, session: filterSession } = filters ?? {
            from_date: fromDate,
            to_date: toDate,
            ingredient: ingredient,
            order_no: orderNo,
            session: session
        };

        setLoading(true);
        setNoRecordsFound(false);
        try {

            let ingredientIds = null;
            if (filterIngredient) {
                // If it's an array (multi-select), map the values
                if (Array.isArray(filterIngredient)) {
                    ingredientIds = filterIngredient.map(item => item.value);
                }
                // If it's a single object (single select)
                else if (filterIngredient.value) {
                    ingredientIds = filterIngredient.value;
                }
            }
            let sessionId = null;
            if (filterSession && filterSession.value && filterSession.value !== "all") {
                sessionId = filterSession.value;
            }

            const response = await axios.post(`${baseUrl}/reports/product_report`, {
                from_date: from_date,
                to_date: to_date,
                ingredient_id: ingredientIds, // Changed to ingredient_id to match backend
                order_no: order_no?.value, // Changed to order_no to match backend
                session_id: sessionId // Changed to session_id to match backend
            });

            setReportData(response.data.data || []);
            console.log("report data:", response.data.data);
            setNoRecordsFound((response.data.data || []).length === 0);
        } catch (error) {
            console.error("Error fetching dairy products report:", error);
            toast.error("Failed to fetch report data");
            setNoRecordsFound(true);
        } finally {
            setLoading(false);
        }
    };

    const displayIssueDetails = async (e, date, ingredient_id, ingredient_name, session_id, session_name) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/dairy_issue/issue_details`, {
                params: {
                    date: date,
                    ingredient_id: ingredient_id,
                    session_id: session_id
                }
            });

            setIssueDetails(response.data.data || []);
            setSelectedDetail({
                date,
                ingredient_id,
                ingredient_name,
                session_id,
                session_name
            });

            setShowIssueModal(true);
        } catch (error) {
            console.error("Error fetching issue details:", error);
            toast.error("Failed to load issue details");
        } finally {
            setLoading(false);
        }
    };

    const displayReturnDetails = async (e, date, ingredient_id, ingredient_name) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/dairy_return/return_details`, {
                params: {
                    date: date,
                    ingredient_id: ingredient_id,
                }
            });

            setReturnDetails(response.data.data || []);
            console.log("return details:", returnDetails)
            setSelectedDetail({
                date,
                ingredient_id,
                ingredient_name,
            });
            setSelectedReturnDetail(returnDetails);

            setShowReturnModal(true);
        } catch (error) {
            console.error("Error fetching issue details:", error);
            toast.error("Failed to load issue details");
        } finally {
            setLoading(false);
        }
    };

    // Callback function when an issue is deleted
    const handleIssueDeleted = async (type) => {
        if (type === 1) {
        // Refresh the issue details
        await displayIssueDetails(
            new Event('click'),
            selectedDetail.date,
            selectedDetail.ingredient_id,
            selectedDetail.ingredient_name,
            selectedDetail.session_id,
            selectedDetail.session_name
        );
    } else if (type === 2) {

        // Refresh return details if it's return
        await displayReturnDetails(
            new Event('click'),
            selectedDetail.date,
            selectedDetail.ingredient_id,
            selectedDetail.ingredient_name
        );
    }

        // Also refresh the main report data
        await getDairyProductsReport();
    };

    // Helper function to get session name
    const getSessionName = (sessionId) => {
        switch (parseInt(sessionId)) {
            case 1: return "Lunch";
            case 2: return "Dinner";
            case 3: return "Breakfast";
            case 4: return "Noon";
            case 5: return "Night";
            default: return "";
        }
    };

    const renderSessionQty = (qty, date, ingredientId, ingredientName, sessionId, sessionName) => {
        // Check if the qty contains a pending indicator 'P'
        const isPending = typeof qty === 'string' && qty.includes('P');
        
        if (isPending) {
            // For pending items - render non-clickable text with orange color
            return (
                <span className="text-orange-500">
                    {qty}
                </span>
            );
        } else {
            // For regular items - render clickable link with blue color
            return (
                <a
                    href="#"
                    className="underline text-blue-500 cursor-pointer"
                    onClick={(e) => displayIssueDetails(e, date, ingredientId, ingredientName, sessionId, sessionName)}
                >
                    {qty}
                </a>
            );
        }
    };

    return (
        <div className="p-4">
            {loading && <Loader />}
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
                    Dairy Products Report
                </h1>
                    <Link to="/">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                    </Link>
                <Toaster position="top-center" reverseOrder={false} />
            </div>

            <div className="relative shadow-md sm:rounded-lg">
                <DairyRequirementFilter handleFilterChange={handelFilterChange} handleClearFilters={() => setFilters({})} page="dairy_products_report" />
            </div>

            {noRecordsFound && <NoRecordsFound />}

            {reportData.length > 0 && (
                <div className="overflow-x-auto mt-5">
                    <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400 border-collapse border border-gray-300">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Date</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Ingredient Name</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Opening Qty.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Order No.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Order Qty.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Total Received qty.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Pending Qty.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center" colSpan="6">Total Issued Qty.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Total Returned Qty.</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Closing Qty.</th>
                            </tr>
                            <tr>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center"></th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center"></th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center"></th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center"></th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Ltrs / Kgs</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Ltrs / Kgs</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">Ltrs / Kgs</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">નાસ્તો</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">લંચ</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">બપોર</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">ડીનર</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">રાત્રે</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center">ટોટલ ઇસ્યુ</th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center"></th>
                                <th scope="col" className="px-2 py-1 border border-gray-300 text-center"></th>
                            </tr>
                            {/* Inline per-column filter row (contains / = / ≤ / ≥ / between / not between) */}
                            <ColumnFilterRow
                                thClassName="px-1 py-1 border border-gray-300 bg-gray-50 align-top"
                                cols={FILTER_COLS.map((c) => ({ key: c.key }))}
                                filters={filters}
                                onChange={setColFilter} />
                        </thead>
                        <tbody>
                            {visibleRows.map((item, index) => (
                                <tr key={index} className="border-b border-gray-300">
                                    <td className="px-2 py-1 border border-gray-300 text-center">{formatDateDMY(item.date)}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.ingredient_name}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.opening_qty}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.order_no}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.order_qty}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.total_received_qty}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{(item.pending_qty).toFixed(2)}</td>
                                    {/* <td className="px-2 py-1 border border-gray-300 text-center">
                                        <a
                                            href="#"
                                            className="underline text-blue-500 cursor-pointer"
                                            onClick={(e) => displayIssueDetails(e, item.date, item.ingredient_id, item.ingredient_name, 3, "Breakfast")}
                                        >
                                            {item.issued_qty.breakfast}
                                        </a>
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        <a
                                            href="#"
                                            className="underline text-blue-500 cursor-pointer"
                                            onClick={(e) => displayIssueDetails(e, item.date, item.ingredient_id, item.ingredient_name, 1, "Lunch")}
                                        >
                                            {item.issued_qty.lunch}
                                        </a>
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        <a
                                            href="#"
                                            className="underline text-blue-500 cursor-pointer"
                                            onClick={(e) => displayIssueDetails(e, item.date, item.ingredient_id, item.ingredient_name, 4, "Noon")}
                                        >
                                            {item.issued_qty.noon}
                                        </a>
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        <a
                                            href="#"
                                            className="underline text-blue-500 cursor-pointer"
                                            onClick={(e) => displayIssueDetails(e, item.date, item.ingredient_id, item.ingredient_name, 2, "Dinner")}
                                        >
                                            {item.issued_qty.dinner}
                                        </a>
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        <a
                                            href="#"
                                            className="underline text-blue-500 cursor-pointer"
                                            onClick={(e) => displayIssueDetails(e, item.date, item.ingredient_id, item.ingredient_name, 5, "Night")}
                                        >
                                            {item.issued_qty.night}
                                        </a>
                                    </td> */}
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        {renderSessionQty(item.issued_qty.breakfast, item.date, item.ingredient_id, item.ingredient_name, 3, "Breakfast")}
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        {renderSessionQty(item.issued_qty.lunch, item.date, item.ingredient_id, item.ingredient_name, 1, "Lunch")}
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        {renderSessionQty(item.issued_qty.noon, item.date, item.ingredient_id, item.ingredient_name, 4, "Noon")}
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        {renderSessionQty(item.issued_qty.dinner, item.date, item.ingredient_id, item.ingredient_name, 2, "Dinner")}
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">
                                        {renderSessionQty(item.issued_qty.night, item.date, item.ingredient_id, item.ingredient_name, 5, "Night")}
                                    </td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.total_issued_qty}</td>
                                    <td className="px-2 py-1 border border-gray-300 text-center"><a
                                        href="#"
                                        className="underline text-blue-500 cursor-pointer"
                                        onClick={(e) => displayReturnDetails(e, item.date, item.ingredient_id, item.ingredient_name)}
                                    >{item.total_returned_qty}
                                    </a></td>
                                    <td className="px-2 py-1 border border-gray-300 text-center">{item.closing_qty}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {showIssueModal && (
                <DairyIssueDetailsModal
                    showModal={showIssueModal}
                    setShowModal={setShowIssueModal}
                    issueDetails={issueDetails}
                    date={selectedDetail.date}
                    ingredient={selectedDetail.ingredient_name}
                    sessionName={selectedDetail.session_name}
                    selectedDetail={selectedDetail}
                    onIssueDeleted={() => handleIssueDeleted(1)}
                    type={1}
                />
            )}
            {showReturnModal && (
                <DairyIssueDetailsModal
                    showModal={showReturnModal}
                    setShowModal={setShowReturnModal}
                    issueDetails={returnDetails}
                    date={selectedDetail.date}
                    ingredient={selectedDetail.ingredient_name}
                    selectedDetail={selectedReturnDetail}
                    onIssueDeleted={() => handleIssueDeleted(2)}
                    type={2}
                />
            )}

        </div>
    );
}

export default DairyProductsReport;